import { useState, useCallback, useEffect, useRef } from "react";
import {
  type SessionRequestState,
  saveSessionRequestState,
  loadSessionRequestState,
  clearSessionRequestState,
  getPendingSessionIds,
} from "@/lib/session-storage";

/**
 * Hook for managing per-session request state with localStorage persistence.
 * This enables:
 * - Loading indicators that persist across session switches
 * - Recovery from mobile app backgrounding
 * - Retry functionality after errors
 */
export function useSessionRequestState(sessionId: string | null) {
  const [state, setState] = useState<SessionRequestState>({ status: "idle" });

  // Track the current session ID to handle switches
  const currentSessionIdRef = useRef(sessionId);

  // Load state from localStorage when session changes
  useEffect(() => {
    currentSessionIdRef.current = sessionId;

    if (!sessionId) {
      setState({ status: "idle" });
      return;
    }

    const stored = loadSessionRequestState(sessionId);
    if (stored) {
      setState(stored);
    } else {
      setState({ status: "idle" });
    }
  }, [sessionId]);

  const setThinking = useCallback(
    (params: { question: string; messageId: string; messageCount: number }) => {
      if (!sessionId) return;

      const newState: SessionRequestState = {
        status: "thinking",
        question: params.question,
        messageId: params.messageId,
        messageCount: params.messageCount,
        startedAt: Date.now(),
      };

      setState(newState);
      saveSessionRequestState(sessionId, newState);
    },
    [sessionId],
  );

  const setError = useCallback(
    (params: { error: string; question: string; messageId: string }) => {
      if (!sessionId) return;

      const newState: SessionRequestState = {
        status: "error",
        error: params.error,
        question: params.question,
        messageId: params.messageId,
      };

      setState(newState);
      saveSessionRequestState(sessionId, newState);
    },
    [sessionId],
  );

  const setIdle = useCallback(() => {
    if (!sessionId) return;

    setState({ status: "idle" });
    clearSessionRequestState(sessionId);
  }, [sessionId]);

  // Clear state for a specific session (useful when deleting sessions)
  const clearForSession = useCallback((targetSessionId: string) => {
    clearSessionRequestState(targetSessionId);
    // If it's the current session, also update local state
    if (targetSessionId === currentSessionIdRef.current) {
      setState({ status: "idle" });
    }
  }, []);

  return {
    state,
    isThinking: state.status === "thinking",
    isError: state.status === "error",
    setThinking,
    setError,
    setIdle,
    clearForSession,
  };
}

/**
 * Hook for getting the list of session IDs with pending requests.
 * Useful for showing indicators in the sidebar.
 * Polls localStorage periodically to stay in sync.
 */
export function usePendingSessionIds(pollIntervalMs = 1000) {
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Initial load
    const updatePendingIds = () => {
      const ids = getPendingSessionIds();
      setPendingIds(new Set(ids));
    };

    updatePendingIds();

    // Poll periodically to catch changes from other actions
    const interval = setInterval(updatePendingIds, pollIntervalMs);

    // Also listen to storage events for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key?.startsWith("chatpod:session:") && e.key.endsWith(":request")) {
        updatePendingIds();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [pollIntervalMs]);

  return pendingIds;
}
